import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
} from "@mui/material";
import { ExchangeOffer, OfferType } from "../../../types/exchange";
import { exchangeService } from "../../../services/exchangeService";
import { useAuth } from "../../../contexts/AuthContext"; // Or however you get the user/token

export const OfferList: React.FC = () => {
  const [offers, setOffers] = useState<ExchangeOffer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newOffer, setNewOffer] = useState({
    offer_type: "buy_usd" as OfferType,
    amount_usd: "",
    rate_bs_per_usd: "",
    location: "",
  });
  const { user } = useAuth();

  // Fetch offers on mount
  useEffect(() => {
    loadOffers();
  }, []);

  const loadOffers = async () => {
    try {
      setLoading(true);
      const data = await exchangeService.listOffers();
      setOffers(data);
      setError(null);
    } catch (err) {
      setError("Failed to load offers");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOffer = async () => {
    try {
      if (!user) {
        setError("Please log in to create an offer");
        return;
      }
      await exchangeService.createOffer({
        ...newOffer,
        amount_usd: parseFloat(newOffer.amount_usd),
        rate_bs_per_usd: parseFloat(newOffer.rate_bs_per_usd),
      });
      setOpenCreateDialog(false);
      setNewOffer({
        offer_type: "buy_usd",
        amount_usd: "",
        rate_bs_per_usd: "",
        location: "",
      });
      setError(null);
      loadOffers(); // Refresh offers list
    } catch (err: any) {
      setError(err?.response?.data?.detail || "Failed to create offer");
    }
  };

  const handleMatchOffer = async (offerId: string) => {
    try {
      if (!user) {
        setError("Please log in to match an offer");
        return;
      }
      await exchangeService.matchOffer(offerId, { payment_method: "qr" });
      loadOffers();
    } catch (err) {
      setError("Failed to match offer");
    }
  };

  if (loading) {
    return <Typography>Loading offers...</Typography>;
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="h4">Exchange Offers</Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setOpenCreateDialog(true)}
        >
          Create Offer
        </Button>
      </Box>

      <Grid container spacing={3}>
        {offers.map((offer) => (
          <Grid item xs={12} md={6} lg={4} key={offer.id}>
            <Card>
              <CardContent>
                <Typography variant="h6" color="primary">
                  {offer.offer_type === "buy_usd" ? "Buy USD" : "Sell USD"}
                </Typography>
                <Typography>Amount: {offer.amount_usd} USD</Typography>
                <Typography>Rate: {offer.rate_bs_per_usd} Bs/USD</Typography>
                <Typography>Location: {offer.location}</Typography>
                <Typography>Status: {offer.status}</Typography>
                {offer.status === "open" && (
                  <Button
                    variant="contained"
                    color="secondary"
                    fullWidth
                    sx={{ mt: 2 }}
                    onClick={() => handleMatchOffer(offer.id)}
                  >
                    Match Offer
                  </Button>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Dialog
        open={openCreateDialog}
        onClose={() => setOpenCreateDialog(false)}
      >
        <DialogTitle>Create New Offer</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Offer Type</InputLabel>
              <Select
                value={newOffer.offer_type}
                label="Offer Type"
                onChange={(e) =>
                  setNewOffer({
                    ...newOffer,
                    offer_type: e.target.value as OfferType,
                  })
                }
              >
                <MenuItem value="buy_usd">Buy USD</MenuItem>
                <MenuItem value="sell_usd">Sell USD</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Amount USD"
              type="number"
              value={newOffer.amount_usd}
              onChange={(e) =>
                setNewOffer({
                  ...newOffer,
                  amount_usd: e.target.value,
                })
              }
              fullWidth
            />

            <TextField
              label="Rate (Bs/USD)"
              type="number"
              value={newOffer.rate_bs_per_usd}
              onChange={(e) =>
                setNewOffer({
                  ...newOffer,
                  rate_bs_per_usd: e.target.value,
                })
              }
              fullWidth
            />

            <TextField
              label="Location"
              value={newOffer.location}
              onChange={(e) =>
                setNewOffer({
                  ...newOffer,
                  location: e.target.value,
                })
              }
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateOffer}
            variant="contained"
            disabled={
              !newOffer.amount_usd ||
              !newOffer.rate_bs_per_usd ||
              !newOffer.location
            }
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
